import React, {ReactNode} from 'react';

// third party
// eslint-disable-next-line import/order
import FroalaEditor, {
  FroalaEvents,
  FroalaOptions,
  ToolbarButtonsType,
} from 'froala-editor';
import 'froala-editor/js/plugins/lists.min';
import 'js-libs/patched-3rd-party/froala-editor/plugins/image';
import 'js-libs/patched-3rd-party/froala-editor/plugins/video';
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/link.min';
import 'froala-editor/js/plugins/draggable.min';
import 'froala-editor/js/plugins/line_breaker.min';
import 'froala-editor/js/plugins/table.min';
import 'froala-editor/js/plugins/paragraph_format.min';
import 'froala-editor/js/plugins/paragraph_style.min';
import 'froala-editor/js/plugins/word_paste.min';
import 'froala-editor/js/languages/es.js';
import 'froala-editor/js/languages/pt_br.js';
import 'froala-editor/js/languages/sv.js';

// custom
import './plugins/image_editor';
import './plugins/documentLinkPlugin';
import './plugins/simple-ol-lists';
import './plugins/normalizeHeadingsPlugin';
import './plugins/confirmOnLeavePlugin';
import './plugins/awsUploadSucessNotifier';
import './plugins/awsUploadSigner';
import './plugins/friendlyErrorMessagesPlugin';
import './plugins/sweetVideos';
import './plugins/preserveUnlinked';
import './plugins/closeEditorWithShortcut';
import './plugins/sweetVideoIcon';
import './plugins/invalidPasteFeedback';
import './plugins/word_counter';
import './plugins/imageCarouselToggle';

import {StyledUserContentWithEditGuidesDiv} from 'app/procedures/styles';
import {
  LOCAL_IMAGE_HOTLINKED_ATTR_NAME,
  LOCAL_IMAGE_URL_ATTR_NAME,
} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import type {AppDispatchType} from 'app/store/AppStore';

type FroalaOptionsType = Partial<{
  toolbarButtonsSM: ToolbarButtonsType;
  toolbarButtonsXS: ToolbarButtonsType;
  toolbarButtons: ToolbarButtonsType;
  imageUploadToS3: boolean;
  videoUpload: boolean;
  events: Partial<FroalaEvents>;
  onCloseRequest: () => unknown; // confirmOnLeavePlugin
}>;

export const ALL_BUTTONS = {
  format: ['bold', 'italic', 'formatOLSweet', 'formatUL'],
  insert: [
    'insertImage',
    'insertVideo',
    'insertLink',
    'insertTable',
    'documentLinkButton',
  ],
  paragraph: [
    'paragraphFormat',
    'underline',
    'alignLeft',
    'alignCenter',
    'alignRight',
    'alignJustify',
    'textColor',
    'backgroundColor',
    'paragraphStyle',
    'clearFormatting',
    'titleCase',
    'Uppercase',
  ],
  meta: ['undo', 'redo'],
};

const DEFAULT_BUTTONS: ToolbarButtonsType = {
  moreText: {
    buttons: [...ALL_BUTTONS.format, ...ALL_BUTTONS.paragraph],
    buttonsVisible: 5,
  },
  moreRich: {
    buttons: ALL_BUTTONS.insert,
    buttonsVisible: 5,
  },
  moreMisc: {buttons: ALL_BUTTONS.meta, align: 'right', buttonsVisible: 2},
};

export function defaultConfig(
  contentType: string
): Partial<FroalaOptions & {wordCounterCount: boolean}> {
  const pluginsEnabled = [
    'align',
    'colors',
    'file',
    'lists',
    'image',
    'link',
    'draggable',
    'table',
    'wordPaste',
    'wordCounter',
    'documentLinkPlugin',
    'normalizeHeadingsPlugin',
    'confirmOnLeavePlugin',
    'awsUploadSucessNotifier',
    'awsUploadSigner',
    'imageEditor',
    'lineBreaker',
    'paragraphFormat',
    'paragraphStyle',
    'friendlyErrorMessages',
    'sweetVideos',
    'preserveUnlinked',
    'closeEditorWithShortcut',
    'sweetVideoIcon',
    'invalidPasteFeedback',
  ];

  if (
    contentType == 'procedureversion' ||
    contentType == 'policyversion' ||
    contentType == 'proceduregroup'
  ) {
    pluginsEnabled.push('video');
  }

  return {
    key: 'Ne2C1sG4H4B3B12C10A7jF1QUg1Xc2OZE1ABVJRDRNGGUH1ITrA1C7A6D6E1D4E4E1C10A6==',
    attribution: false,
    iconsTemplate: 'font_awesome_5',

    charCounterCount: false,
    imageAddNewLine: true,
    listAdvancedTypes: true,

    // buttons
    toolbarButtons: DEFAULT_BUTTONS,
    linkInsertButtons: [],
    linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
    linkConvertEmailAddress: false,
    imageInsertButtons: ['imageBack', '|', 'imageUpload', 'imageByURL'],
    imageEditButtons: [
      'imageReplace',
      'imageAlign',
      'imageRemove',
      '|',
      'imageLink',
      'linkOpen',
      'linkEdit',
      'linkRemove',
      '-',
      'imageDisplay',
      'imageCarouselToggle',
      'imageSize',
      'imageEditor',
    ],

    // image
    imageAllowedTypes: [
      'gif',
      'jpg',
      'jpeg',
      'bmp',
      'png',
      'ico',
      'tiff',
      'tif',
      'eps',
    ],
    imageResizeWithPercent: true,
    imageRoundPercent: true,
    imageDefaultWidth: 0,
    imageUploadRemoteUrls: false,

    // video
    videoAllowedProviders: ['youtube', 'vimeo', 'screencast'],
    videoAllowedTypes: ['mp4', 'webm'],
    videoInsertButtons: ['videoBack', '|', 'videoUpload', 'videoByURL'],
    videoDefaultWidth: 600,
    videoMaxSize: 1024 * 1024 * 50,
    videoMove: false,
    videoResize: true,
    videoTextNear: false,
    videoUpload: true,

    // table
    tableResizer: true,
    tableCellStyles: {},
    tableStyles: {},
    tableEditButtons: [
      'tableHeader',
      'tableRemove',
      '|',
      'tableRows',
      'tableColumns',
      'tableCellHorizontalAlign',
      'tableCells',
    ],
    lineBreakerTags: ['table'],
    lineBreakerOffset: 5,

    // HTML cleaning
    htmlAllowComments: false,
    htmlAllowedAttrs: [
      'href',
      'rel',
      'target',
      'src',
      'width',
      'height',
      'alt',
      'class',
      'style',
      'controls',
      'unlinked',
      'rowspan',
      'colspan',
      'allowfullscreen',
      'frameborder',
      LOCAL_IMAGE_URL_ATTR_NAME,
      LOCAL_IMAGE_HOTLINKED_ATTR_NAME,
    ],
    htmlAllowedTags: [
      'a',
      'b',
      'blockquote',
      'em',
      'i',
      'li',
      'ol',
      'span',
      'strong',
      'u',
      'ul',
      'br',
      'pre',
      'p',
      'div',
      'img',
      'iframe',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'table',
      'tr',
      'th',
      'td',
      'tbody',
      'thead',
    ],
    wordAllowedStyleProps: ['list-style-type'],
    wordPasteModal: false,
    wordCounterCount: false,

    heightMin: 200,
    // enable tab indenting
    tabSpaces: 4,
    enter: FroalaEditor.ENTER_BR,
    linkAlwaysBlank: true,
    paragraphFormat: {
      N: gt('Normal'),
      H3: gt('Heading'),
      H4: gt('Heading 2'),
      H5: gt('Heading 3'),
      PRE: gt('Quote'),
    },
    paragraphStyles: {
      'alert alert-success': gt('Success'),
      'alert alert-primary': gt('Info'),
      'alert alert-secondary': gt('Secondary'),
      'alert alert-warning': gt('Warning'),
      'alert alert-danger': gt('Danger'),
    },
    paragraphMultipleStyles: false,
    shortcutsHint: true,

    pluginsEnabled,
  };
}

export type Props = {
  value?: string;
  mediaURL: string;
  mediaURLRead: string;
  language: string;
  events?: {[key: string]: (...any) => any};
  children?: ReactNode;
  onChange: (string) => void;
  reduxDispatch: AppDispatchType;
  onEditorIsBusyChange?: (boolean) => void;
  imagesS3UploadConfig: any;
  videosS3UploadConfig: any;
  accountId: number;
  options: FroalaOptionsType;
  contentType: string;
  objectId: number | string;
  onInitialized?: () => unknown;
  scrollableContainer?: string;
};

class PreparedFroalaEditor extends React.PureComponent<Props> {
  editorInitialized = false;
  oldModel: string | void;

  elRef = React.createRef<HTMLDivElement>();
  $element: any;
  editor: FroalaEditor;

  constructor(props: Props) {
    super(props);
    this.oldModel = props.value;
  }

  componentDidMount(): void {
    this.createEditor();
  }

  componentWillUnmount(): void {
    this.destroyEditor();
  }

  createEditor(): void {
    if (this.editorInitialized || this.elRef.current == null) {
      return;
    }

    const config: Partial<FroalaOptions> = {
      ...defaultConfig(this.props.contentType),
      language: this.props.language.replace('-', '_'),
      onEditorIsBusyChange: this.props.onEditorIsBusyChange,
      imageUploadToS3: this.props.imagesS3UploadConfig,
      videoUploadToS3: this.props.videosS3UploadConfig,
      imageEditorExtra: {
        mediaURL: this.props.mediaURL,
        mediaURLRead: this.props.mediaURLRead,
      },
      sweetprocess: {
        contentType: this.props.contentType,
        objectId: this.props.objectId,
        accountId: this.props.accountId,
        mediaURL: this.props.mediaURL,
        reduxDispatch: this.props.reduxDispatch,
      },
      ...this.props.options,
      events: {
        ...(this.props.options.events || {}),
        ...this.getEvents(),
      },
    };

    FroalaEditor.RegisterShortcut(
      FroalaEditor.KEYCODE.ONE,
      'paragraphFormat',
      'H3',
      '1',
      false,
      true
    ); // Ctrl + Alt + 1 for Heading
    FroalaEditor.RegisterShortcut(
      FroalaEditor.KEYCODE.TWO,
      'paragraphFormat',
      'H4',
      '2',
      false,
      true
    ); // Ctrl + Alt + 2 for Heading 2
    FroalaEditor.RegisterShortcut(
      FroalaEditor.KEYCODE.THREE,
      'paragraphFormat',
      'H5',
      '3',
      false,
      true
    ); // Ctrl + Alt + 3 for Heading 3
    FroalaEditor.RegisterShortcut(
      FroalaEditor.KEYCODE.Q,
      'paragraphFormat',
      'PRE',
      'Q',
      false,
      true
    ); // Ctrl + Alt + Q for Quote
    FroalaEditor.RegisterShortcut(
      FroalaEditor.KEYCODE.N,
      'paragraphFormat',
      'N',
      'N',
      false,
      true
    );
    FroalaEditor.DefineIcon('upperCaseIcon', {
      NAME: 'fas fa-font',
    });
    FroalaEditor.RegisterCommand('Uppercase', {
      title: gt('Uppercase'),
      icon: 'upperCaseIcon',
      focus: true,
      undo: true,
      refreshAfterCallback: true,
      callback: () => {
        globalThis.editor.undo.saveStep();
        const selected = globalThis.editor.selection.text();
        globalThis.editor.html.insert(selected.toUpperCase());
      },
    });
    FroalaEditor.DefineIcon('titleCase', {NAME: 'fas fa-text-height'});
    FroalaEditor.RegisterCommand('titleCase', {
      title: gt('Title Case'),
      icon: 'titleCase',
      focus: true,
      undo: true,
      refreshAfterCallback: true,
      callback: () => {
        globalThis.editor.undo.saveStep();
        const selected = globalThis.editor.selection.text();
        const titleCased = selected.replace(
          /\w\S*/g,
          (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
        globalThis.editor.html.insert(titleCased);
      },
    });
    FroalaEditor.RegisterShortcut(
      FroalaEditor.KEYCODE.F3,
      'Uppercase',
      '',
      'F3',
      false,
      true
    );

    globalThis.editor = new FroalaEditor(this.elRef.current, config, () => {
      this.setHTML();
      this.props.onInitialized?.();
    });
    this.editor = globalThis.editor;
    this.editorInitialized = true;
  }

  setHTML = (): void => {
    this.editor.html.set(this.props.value || '');
    this.editor.undo.reset();
    this.editor.undo.saveStep();
  };

  getEvents(): {[key: string]: (...any) => any} {
    return {
      'link.beforeRemove': () => false,
      contentChanged: this.updateModel,
      destroy: () => this.updateModel(true),
    };
  }

  updateModel = (destroying?: boolean): void => {
    if (!this.props.onChange) {
      return;
    }

    if (!destroying || this.editor.html != null) {
      const html = this.editor.html.get();
      this.oldModel = typeof html === 'string' ? html : '';
      this.props.onChange(this.oldModel);
    }
  };

  destroyEditor(): void {
    if (this.elRef.current != null && this.editorInitialized) {
      this.editor.destroy();
      this.editorInitialized = false;
    }
  }

  render(): ReactNode {
    return (
      <StyledUserContentWithEditGuidesDiv
        className={
          isRightToLeftWrapper(this.props.value || '')
            ? 'right-to-left-text'
            : ''
        }
        ref={this.elRef}
      >
        {this.props.children}
      </StyledUserContentWithEditGuidesDiv>
    );
  }
}

export default PreparedFroalaEditor;
